import { useState } from "react";
import { toast } from "sonner";
import { MapPin, Gavel, Users, Lock, FileDown } from "lucide-react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import CountdownMeter from "@/components/CountdownMeter";
import { buyDownload } from "@/pages/Galleria";

const TIERS = [
  { price: 80, name: "Aperitivo d'Arte", items: ["9 finger food ispirati alle opere", "Percorso tra i quadri", "Asta live inclusa"] },
  { price: 160, name: "Cena in Tre Atti", items: ["Cena di 3 portate", "Vino in abbinamento", "Canapè di benvenuto", "Asta live inclusa"] },
  { price: 369, name: "Esperienza Totale", items: ["Cena di 6 portate", "Vino e acqua", "Canapè di benvenuto", "Drink di fine cena", "Asta live inclusa"] },
];

const CAPARRA_LINK = "https://buy.stripe.com/14AaEW8em9v0bDkaWx6AM0j";

const GuidaModal = ({ open, onOpenChange }) => {
  const [form, setForm] = useState({ nome: "", email: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/guida-assaggi", form);
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nell'invio, riprova tra qualche minuto.");
    } finally {
      setSending(false);
    }
  };

  const close = (v) => {
    onOpenChange(v);
    if (!v) setTimeout(() => { setDone(false); setForm({ nome: "", email: "" }); }, 300);
  };

  const cls = "w-full bg-[#111] border border-[#C9A84C]/30 focus:border-[#C9A84C] outline-none px-4 py-3 text-sm text-[#F5F0E8] placeholder-[#777] transition-colors";
  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="bg-[#1A1A1A] border border-[#C9A84C]/40 text-[#F5F0E8]" data-testid="guida-modal">
        <DialogHeader>
          <DialogTitle className="font-display">Ricevi la guida gratuita</DialogTitle>
          <DialogDescription className="text-[#9a9484]">Inserisci i tuoi dati e ti inviamo la guida via email.</DialogDescription>
        </DialogHeader>
        {done ? (
          <div className="border border-[#C9A84C] bg-[#C9A84C]/10 px-5 py-7 text-center" data-testid="guida-success">
            <p className="font-bold leading-relaxed">✅ Perfetto! Controlla la tua email — la guida è in arrivo.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <input type="text" required minLength={2} value={form.nome}
              onChange={(e) => setForm({ ...form, nome: e.target.value })}
              placeholder="Il tuo nome" className={cls} data-testid="guida-nome-input" />
            <input type="email" required value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="La tua email" className={cls} data-testid="guida-email-input" />
            <button type="submit" disabled={sending} data-testid="guida-submit-btn"
              className="w-full font-bold uppercase tracking-wider text-sm py-3 transition-opacity hover:opacity-85 disabled:opacity-60"
              style={{ background: "#C9A84C", color: "#1A1A1A" }}>
              {sending ? "Invio in corso…" : "Ricevi la guida →"}
            </button>
            <p className="text-[11px] text-[#9a9484] text-center">Nessuno spam. Solo aggiornamenti sulle serate Wildersquad.</p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default function AssaggiArte() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", city: "", tier: 160 });
  const [loading, setLoading] = useState(false);
  const [guidaOpen, setGuidaOpen] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/waitlist", {
        ...form,
        tier: Number(form.tier),
        num_people: 1,
      });
      window.location.href = CAPARRA_LINK;
    } catch {
      toast.error("Qualcosa è andato storto. Controlla i dati e riprova.");
      setLoading(false);
    }
  };

  return (
    <div data-testid="assaggi-page">
      <section className="border-b border-[#2A2A2A]">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16 grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-[#E4BD44] mb-4">Il format Wildersquad</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-6">Assaggi d'Arte</h1>
            <p className="text-base text-[#C9C4B8] leading-relaxed mb-8 max-w-lg">
              La cena-evento firmata dallo <strong>Chef Antonio Blandolino</strong>, ispirata alle opere di suo padre
              Italo, in una location segreta nel cuore del <strong>Salento</strong>. Ogni portata nasce da un quadro,
              tra la terra, il mare e l'arte salentina. A fine serata, asta live delle opere.
            </p>
            <button onClick={() => setGuidaOpen(true)} data-testid="scarica-guida-btn"
               className="btn-outline !py-2.5 !px-5 text-xs inline-flex mb-8">
              <FileDown size={14} /> Scarica la guida
            </button>
            <GuidaModal open={guidaOpen} onOpenChange={setGuidaOpen} />
            <div className="grid grid-cols-2 gap-6 text-sm">
              {[
                [MapPin, "Nel cuore del Salento", "Cucina d'autore, radici vere"],
                [Lock, "Location segreta", "Rivelata a quota raggiunta"],
                [Users, "Min. 30 paganti", "Caparra fissa €36,90"],
                [Gavel, "Asta live", "Riproduzioni numerate + NFT"],
              ].map(([Icon, t, d]) => (
                <div key={t} className="flex gap-3">
                  <Icon size={18} className="text-[#E4BD44] shrink-0 mt-0.5" />
                  <div><p className="font-semibold">{t}</p><p className="text-xs text-[#9a9484]">{d}</p></div>
                </div>
              ))}
            </div>
          </div>
          <div className="flex flex-col justify-center">
            <div className="art-frame aspect-video border border-[#2A2A2A] mb-8">
              <img src="https://images.unsplash.com/photo-1536392706976-e486e2ba97af?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400" alt="Tavola Assaggi d'Arte" />
            </div>
            <CountdownMeter />
          </div>
        </div>
      </section>

      <section className="border-b border-[#2A2A2A] bg-[#141414]">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16">
          <h2 className="font-display text-lg font-bold mb-10">Tre fasce, una sola regola: lasciarsi stupire</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#2A2A2A] border border-[#2A2A2A]">
            {TIERS.map((t) => (
              <div key={t.price} className="bg-[#0A0A0A] p-8" data-testid={`tier-${t.price}`}>
                <p className="font-display text-5xl font-black mb-1">€{t.price}</p>
                <p className="text-xs uppercase tracking-[0.2em] text-[#E4BD44] mb-6">{t.name} · a persona</p>
                <ul className="space-y-2.5 text-sm text-[#C9C4B8]">
                  {t.items.map((it) => (
                    <li key={it} className="flex gap-2"><span className="text-[#D4AF37]">—</span>{it}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-[#2A2A2A]">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16">
          <p className="text-xs uppercase tracking-[0.25em] text-[#E4BD44] mb-3">La cucina d'autore</p>
          <h2 className="font-display text-lg font-bold mb-2">Assaggi dal percorso</h2>
          <p className="text-sm text-[#C9C4B8] leading-relaxed max-w-2xl mb-10">
            Ogni serata è un menu unico. Ecco alcuni dei piatti firmati Chef Antonio Blandolino.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="assaggi-cucina-gallery">
            {[
              ["https://customer-assets-m6fa6gv7.emergentagent.net/wingman/a7d790a2-0920-433d-b5d4-1e925b54849f/attachments/6dc6974d249d4544a2f87b65a4f4882b_zeppole%20allo%20zafferano%2C%20tartare%20di%20gamberi%2C%20ricotta%20scante%20%2C%20lampone%20e%20semi%20di%20canapa%20decorticati.jpeg", "Zeppole allo zafferano, tartare di gamberi, ricotta scante e lampone"],
              ["https://customer-assets-m6fa6gv7.emergentagent.net/wingman/a7d790a2-0920-433d-b5d4-1e925b54849f/attachments/5cb2ad72fc7d4b24b35feaa99326f77b_pan%20brioche%20con%20tartare%20di%20manzo%2C%20salsa%20cesar%2C%20maio%20agli%20agrumi.jpg", "Pan brioche con tartare di manzo, salsa cesar e maio agli agrumi"],
              ["https://customer-assets-m6fa6gv7.emergentagent.net/wingman/a7d790a2-0920-433d-b5d4-1e925b54849f/attachments/65ea1cbfcb2f4e7c8229a2b792a3f4ad_piramidine%20ripiene%20di%20lingua%20di%20manzo%2C%20spuma%20di%20patate%20e%20demiglace%2C%20con%20polvere%20di%20barbabietola.jpg", "Piramidine di lingua di manzo, spuma di patate, demi-glace e barbabietola"],
              ["https://customer-assets-m6fa6gv7.emergentagent.net/wingman/a7d790a2-0920-433d-b5d4-1e925b54849f/attachments/cf9170d9634f48b2b4a67b49985a38e6_spaghetti%20freddi%20alla%20marinara%2C%20arancia%2C%20tapioca%20e%20limone%20.jpeg", "Spaghetti freddi alla marinara, arancia, tapioca e limone"],
              ["https://customer-assets-m6fa6gv7.emergentagent.net/wingman/a7d790a2-0920-433d-b5d4-1e925b54849f/attachments/f206f1e98949411c824329723c2914c3_millefoglie%20di%20patate%20al%20peperoncino%2C%20crema%20di%20patate%2C%20verza%20e%20cips%20di%20patate%20viola.jpeg", "Millefoglie di patate al peperoncino, crema di patate, verza e chips viola"],
              ["https://customer-assets-m6fa6gv7.emergentagent.net/wingman/a7d790a2-0920-433d-b5d4-1e925b54849f/attachments/992f38747cac4942b1a745fe333231d9_pezzetti%20di%20asina%20in%20salsa%20pomodoro%2C%20spuma%20di%20patate%20basilico.jpeg", "Pezzetti di asina in salsa pomodoro, spuma di patate al basilico"],
              ["https://customer-assets-m6fa6gv7.emergentagent.net/wingman/a7d790a2-0920-433d-b5d4-1e925b54849f/attachments/95d44cde8a3241e5b4b6d2f57dd82113_insalata%20di%20farro%20multicolor.jpeg", "Insalata di farro multicolor"],
              ["https://customer-assets-m6fa6gv7.emergentagent.net/wingman/a7d790a2-0920-433d-b5d4-1e925b54849f/attachments/203ce2fc76574cb8b4984ddbdc80dc79_photo_2026-01-31%2022.42.03.jpeg", "Dal passe dello Chef Antonio"],
            ].map(([src, cap]) => (
              <figure key={src}>
                <div className="art-frame aspect-square border border-[#2A2A2A] overflow-hidden">
                  <img src={src} alt={cap} loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                </div>
                <figcaption className="text-xs text-[#9a9484] mt-2 font-mono-ws">{cap}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-[#2A2A2A]">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-4">
            <div className="art-frame aspect-square border border-[#2A2A2A]">
              <img src="https://customer-assets-m6fa6gv7.emergentagent.net/job_wildersquad-hub/artifacts/2hfynt71_c0861793-b171-44d4-a828-c48e1d28b781.jpg" alt="Chef Antonio Blandolino con il dentice" />
            </div>
            <p className="text-xs text-[#9a9484] mt-3 font-mono-ws">Chef Antonio Blandolino — materia prima, prima di tutto</p>
          </div>
          <div className="lg:col-span-8">
            <p className="text-xs uppercase tracking-[0.25em] text-[#E4BD44] mb-3">In cucina</p>
            <h2 className="font-display text-lg font-bold mb-2">Lo Chef Antonio Blandolino</h2>
            <p className="text-sm text-[#C9C4B8] leading-relaxed max-w-2xl mb-10">
              Figlio d'arte in ogni senso: Antonio trasforma i quadri di suo padre Italo in piatti che si guardano
              prima di essere mangiati. Ecco alcune delle sue creazioni.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-5" data-testid="chef-dishes">
              {[
                ["https://customer-assets-m6fa6gv7.emergentagent.net/job_wildersquad-hub/artifacts/0jttun6w_photo_2025-11-02%2021.43.08.jpeg", "Pasta ai broccoli, crema affumicata e polvere di olive"],
                ["https://customer-assets-m6fa6gv7.emergentagent.net/job_wildersquad-hub/artifacts/7hn93xbr_photo_2025-12-03%2016.02.49.jpeg", "Spirale di salsiccia, polenta cremosa e funghi porcini"],
                ["https://customer-assets-m6fa6gv7.emergentagent.net/job_wildersquad-hub/artifacts/p3oz7pj1_photo_2025-12-04%2009.36.20.jpeg", "Brasato in crosta, brunoise di verdure e spuma ai frutti rossi"],
                ["https://customer-assets-m6fa6gv7.emergentagent.net/job_wildersquad-hub/artifacts/bhdvgvuh_bombette%20salentine%20di%20capocollo%20di%20maiale%2C%20ripiene%20di%20cotto%20e%20scamorza%2C%20crema%20di%20castagne%20lampone%20ghiacciato.jpg", "Bombette salentine di capocollo, cotto e scamorza, crema di castagne e lampone ghiacciato"],
                ["https://customer-assets-m6fa6gv7.emergentagent.net/job_wildersquad-hub/artifacts/wztbwtiz_dentice%20con%20crosta%20croccante%20alle%20erbette%20crema%20di%20sedano%20rapa%20e%20asparagi%20saltati.jpg", "Dentice in crosta croccante alle erbette, crema di sedano rapa e asparagi saltati"],
                ["https://customer-assets-m6fa6gv7.emergentagent.net/job_wildersquad-hub/artifacts/14r9nfhg_uovo%20al%20vapore%20su%20rosti%20croccante%20al%20peperoncino%2C%20spuma%20di%20piselli%2C%20salsa%20al%20grana%20padano%2Cbacon%20croccante%20e%20piselli.webp", "Uovo al vapore su rösti al peperoncino, spuma di piselli, grana padano e bacon croccante"],
                ["https://customer-assets-m6fa6gv7.emergentagent.net/job_wildersquad-hub/artifacts/l122j7ib_burrino%20alla%20rapa%20rossa%20.jpeg", "Burrino alla rapa rossa"],
                ["https://customer-assets-m6fa6gv7.emergentagent.net/job_wildersquad-hub/artifacts/okh7olci_filetto%20di%20maiale%2C%20purea%20di%20patate%2C%20zucca%20saltata%20e%20jus%20di%20manzo.jpeg", "Filetto di maiale, purea di patate, zucca saltata e jus di manzo"],
                ["https://customer-assets-m6fa6gv7.emergentagent.net/job_wildersquad-hub/artifacts/ogw3yyhu_lingotto%20di%20costine%20di%20maiale%2C%20crema%20di%20cipolle%20dolci%2C%20crumble%20salato%20al%20parmigiano.jpeg", "Lingotto di costine di maiale, crema di cipolle dolci e crumble al parmigiano"],
                ["https://customer-assets-m6fa6gv7.emergentagent.net/job_wildersquad-hub/artifacts/g7tpiytb_maccheroni%20al%20pomodoro%20e%20basilico%2C%20crema%20di%20burrata.jpeg", "Maccheroni al pomodoro e basilico, crema di burrata"],
                ["https://customer-assets-m6fa6gv7.emergentagent.net/job_wildersquad-hub/artifacts/3rutl8ve_merluzzo%20al%20vapore%20marinato%20ai%203%20pepi%2Cspinacino%2C%20zuccca%2C%20dressing%20al%20curry.jpeg", "Merluzzo al vapore ai 3 pepi, spinacino, zucca e dressing al curry"],
              ].map(([src, cap], i) => (
                <figure key={cap} data-testid={`chef-dish-${i}`}>
                  <div className="art-frame aspect-square border border-[#2A2A2A]">
                    <img src={src} alt={cap} loading="lazy" />
                  </div>
                  <figcaption className="text-xs text-[#9a9484] mt-2 font-mono-ws leading-snug">{cap}</figcaption>
                  <div className="flex gap-2 mt-2">
                    <button onClick={() => buyDownload({ title: cap, image_url: src }, "jpg")}
                      className="btn-outline !py-1 !px-2.5 text-[10px]" data-testid={`dish-download-jpg-${i}`}>
                      JPG €9
                    </button>
                    <button onClick={() => buyDownload({ title: cap, image_url: src }, "pdf")}
                      className="btn-outline !py-1 !px-2.5 text-[10px]" data-testid={`dish-download-pdf-${i}`}>
                      PDF €9
                    </button>
                  </div>
                </figure>
              ))}
            </div>
            <p className="text-[10px] text-[#9a9484] leading-relaxed mt-6 max-w-2xl" data-testid="dishes-copyright-clause">
              © Tutti i diritti riservati. Le fotografie dei piatti e delle opere sono protette da diritto d'autore:
              l'acquisto della copia digitale (€9) concede una licenza d'uso personale e non commerciale.
              È vietato il riutilizzo, la riproduzione e la ridistribuzione senza licenza e royalty concordate con Wildersquad.
            </p>
          </div>
        </div>
      </section>

      <section>
        <div className="max-w-3xl mx-auto px-5 lg:px-10 py-16">
          <h2 className="font-display text-lg font-bold mb-2">Entra in lista d'attesa</h2>
          <p className="text-sm text-[#C9C4B8] mb-8">
            Blocca il tuo posto con una caparra di <strong>€36,90</strong> (via Stripe). Scalata dal prezzo finale della serata.
          </p>
          <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="waitlist-form">
            <input required className="ws-input" placeholder="Nome e cognome" value={form.name} onChange={set("name")} data-testid="waitlist-name" />
            <input required type="email" className="ws-input" placeholder="Email" value={form.email} onChange={set("email")} data-testid="waitlist-email" />
            <input className="ws-input" placeholder="Telefono (opzionale)" value={form.phone} onChange={set("phone")} data-testid="waitlist-phone" />
            <input required className="ws-input" placeholder="Città desiderata" value={form.city} onChange={set("city")} data-testid="waitlist-city" />
            <select className="ws-input" value={form.tier} onChange={set("tier")} data-testid="waitlist-tier">
              <option value={80}>Aperitivo d'Arte — €80</option>
              <option value={160}>Cena in Tre Atti — €160</option>
              <option value={369}>Esperienza Totale — €369</option>
            </select>
            <div className="sm:col-span-2 flex flex-col sm:flex-row items-start sm:items-center justify-end gap-4 mt-2">
              <button type="submit" disabled={loading} className="btn-primary" data-testid="waitlist-submit">
                {loading ? "Reindirizzamento…" : "PRENOTA CON CAPARRA €36,90 →"}
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
